import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Handle, Position } from "@xyflow/react";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import emojiData from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { FlowVariableInsertPicker } from "../../components/flow-variables";

export const InteractiveMessageNode = ({ data, id, selected = false }) => {
  // Safeguard against missing data prop
  const safeData = data || {};

  // Helper to normalize labels into plain strings for editing
  const normalizeLabelsToStrings = (labels) => {
    if (!labels || !Array.isArray(labels)) return [];
    return labels.map((label) => {
      if (typeof label === "object" && label !== null) {
        return label.label || "";
      }
      return label || "";
    });
  };
  const [showMenu, setShowMenu] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const menuRef = useRef(null);
  const bodyTextareaRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const availableVariables = Array.isArray(safeData.availableVariables)
    ? safeData.availableVariables
    : [];
  // Edit popup state
  const [editHeader, setEditHeader] = useState(safeData.header || "");
  const [editBody, setEditBody] = useState(safeData.body || "");
  const [editFooter, setEditFooter] = useState(safeData.footer || "");
  const [editButtons, setEditButtons] = useState(
    normalizeLabelsToStrings(safeData.labels)
  );
  const [editSections, setEditSections] = useState(safeData.sections || []);
  const [editListHeader, setEditListHeader] = useState(
    safeData.listHeader || ""
  );
  const [editMessageType, setEditMessageType] = useState(
    safeData.messageType || "buttons"
  );
  const [editAnswerVariable, setEditAnswerVariable] = useState(
    safeData.answerVariable || ""
  );

  // Validation errors
  const [headerError, setHeaderError] = useState("");
  const [bodyError, setBodyError] = useState("");
  const [footerError, setFooterError] = useState("");
  const [buttonErrors, setButtonErrors] = useState([]);
  const [answerVariableError, setAnswerVariableError] = useState("");

  // Check if a handle is connected by looking at the edges
  const isHandleConnected = useCallback(
    (handleId) => {
      if (!safeData.edges || !Array.isArray(safeData.edges)) {
        console.log(`[${id}] No edges data for handle ${handleId}`);
        return false;
      }

      // Check if any edge uses this handle as a source
      const isConnected = safeData.edges.some(
        (edge) => edge.sourceHandle === handleId
      );

      return isConnected;
    },
    [safeData.edges, id]
  );

  // Log when edges change to debug connection state
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && data.edges?.length > 0) {
      console.log(`[${id}] Edges updated:`, {
        edgesCount: data.edges?.length || 0,
        lastEdgeUpdate: data.lastEdgeUpdate,
      });
    }
  }, [data.edges, data.lastEdgeUpdate, id]);

  // Log when component re-renders (only in development)
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(`[${id}] Interactive Message Node re-rendered`);
    }
  });

  // Simplified logging - only log when popup opens
  useEffect(() => {
    if (showEditPopup) {
      console.log("=== Edit Popup Opened ===", { id });
    }
  }, [showEditPopup, id]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  const [header, setHeader] = useState(safeData.header || "");
  const [body, setBody] = useState(safeData.body || "");
  const [footer, setFooter] = useState(safeData.footer || "");
  // Always store labels as plain strings
  const [labels, setLabels] = useState(
    normalizeLabelsToStrings(safeData.labels)
  );
  const [sections, setSections] = useState(safeData.sections || []);
  const [listHeader, setListHeader] = useState(safeData.listHeader || "");
  const [messageType, setMessageType] = useState(
    safeData.messageType || "buttons"
  );
  const [answerVariable, setAnswerVariable] = useState(
    safeData.answerVariable || ""
  );

  // Notify parent about section changes to trigger child node creation
  const notifySectionsChange = useCallback(
    (newSections) => {
      if (safeData.onSectionsChange && messageType === "list") {
        // Ensure we have a valid position, fallback to default if not available
        const currentPosition = safeData.position || { x: 0, y: 0 };
        safeData.onSectionsChange(id, newSections, currentPosition);
      }
    },
    [safeData.onSectionsChange, id, messageType, safeData.position]
  );

  // Sync local state with data prop changes
  useEffect(() => {
    console.log(`[${id}] Syncing with data prop:`, {
      header: safeData.header,
      body: safeData.body,
      footer: safeData.footer,
      labels: safeData.labels,
      sections: safeData.sections,
      listHeader: safeData.listHeader,
      messageType: safeData.messageType,
    });
    
    setHeader(safeData.header || "");
    setBody(safeData.body || "");
    setFooter(safeData.footer || "");
    // Normalize incoming labels to strings so render never sees label objects
    setLabels(normalizeLabelsToStrings(safeData.labels));
    setSections(safeData.sections || []);
    setListHeader(safeData.listHeader || "");
    setMessageType(safeData.messageType || "buttons");
    setAnswerVariable(safeData.answerVariable || "");
  }, [
    safeData.header,
    safeData.body,
    safeData.footer,
    safeData.labels,
    safeData.sections,
    safeData.listHeader,
    safeData.messageType,
    safeData.answerVariable,
  ]);

  // Notify parent about section changes to trigger child node creation
  useEffect(() => {
    if (messageType === "list" && sections && Array.isArray(sections)) {
      notifySectionsChange(sections);
    } else if (messageType !== "list") {
      // Clear child nodes if not list type
      if (safeData.onSectionsChange) {
        const currentPosition = safeData.position || { x: 0, y: 0 };
        safeData.onSectionsChange(id, [], currentPosition);
      }
    }
  }, [
    sections,
    messageType,
    safeData.position,
    notifySectionsChange,
    safeData.onSectionsChange,
    id,
  ]);

  // Log connection state for each button when edges change
  useEffect(() => {
    if (labels && Array.isArray(labels)) {
      labels.forEach((label, index) => {
        const handleId = id + "-label-" + index;
        const isConnected = isHandleConnected(handleId);
        if (isConnected) {
          console.log(
            `[${id}] Button ${index} (${label}) is connected after edge change`
          );
        }
      });
    }
  }, [data.edges, data.lastEdgeUpdate, labels, id, isHandleConnected]);

  // Validation functions
  const validateHeader = (value) => {
    if (value.length > 60) {
      setHeaderError("Header cannot exceed 60 characters");
      return false;
    }
    setHeaderError("");
    return true;
  };

  const validateBody = (value) => {
    if (value.length > 1024) {
      setBodyError("Body cannot exceed 1024 characters");
      return false;
    }
    setBodyError("");
    return true;
  };

  const validateFooter = (value) => {
    if (value.length > 60) {
      setFooterError("Footer cannot exceed 60 characters");
      return false;
    }
    setFooterError("");
    return true;
  };

  const validateButton = (value, index) => {
    if (value.length > 20) {
      const newErrors = [...buttonErrors];
      newErrors[index] = "Button text cannot exceed 20 characters";
      setButtonErrors(newErrors);
      return false;
    }
    const newErrors = [...buttonErrors];
    newErrors[index] = "";
    setButtonErrors(newErrors);
    return true;
  };

  const validateListHeader = (value) => {
    if (value.length > 60) {
      return false;
    }
    return true;
  };

  const validateAnswerVariable = (value) => {
    if (!value) {
      setAnswerVariableError("");
      return true;
    }
    const isValid = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value);
    if (!isValid) {
      setAnswerVariableError(
        "Use letters, numbers, and underscore; cannot start with a number"
      );
      return false;
    }
    setAnswerVariableError("");
    return true;
  };

  const LIST_SECTION_TITLE_MAX = 24;
  const LIST_ROW_TITLE_MAX = 24;
  const LIST_ROW_DESCRIPTION_MAX = 72;

  const validateSectionTitle = (value) =>
    !value || value.length <= LIST_SECTION_TITLE_MAX;
  const validateRowTitle = (value) =>
    !value || value.length <= LIST_ROW_TITLE_MAX;
  const validateRowDescription = (value) =>
    !value || value.length <= LIST_ROW_DESCRIPTION_MAX;

  // Handle input changes with validation
  const handleHeaderChange = (e) => {
    const value = e.target.value;
    setEditHeader(value);
    validateHeader(value);
  };

  const handleBodyChange = (e) => {
    const value = e.target.value;
    setEditBody(value);
    validateBody(value);
  };

  const handleFooterChange = (e) => {
    const value = e.target.value;
    setEditFooter(value);
    validateFooter(value);
  };

  const handleButtonChange = (index, value) => {
    const newButtons = [...editButtons];
    newButtons[index] = value;
    setEditButtons(newButtons);
    validateButton(value, index);
  };

  const handleListHeaderChange = (e) => {
    const value = e.target.value;
    if (validateListHeader(value)) {
      setEditListHeader(value);
    }
  };

  // Button management
  const addButton = () => {
    if (editButtons.length < 3) {
      setEditButtons([...editButtons, ""]);
      setButtonErrors([...buttonErrors, ""]);
    }
  };

  const deleteButton = (index) => {
    const newButtons = editButtons.filter((_, i) => i !== index);
    const newErrors = buttonErrors.filter((_, i) => i !== index);
    setEditButtons(newButtons);
    setButtonErrors(newErrors);
  };

  // List Section Management Functions
  const addSection = () => {
    if (editSections.length < 10) {
      const newSection = {
        id: Date.now() + Math.random(),
        title: "",
        rows: [],
      };
      setEditSections([...editSections, newSection]);
    }
  };

  const deleteSection = (sectionIndex) => {
    const newSections = editSections.filter((_, i) => i !== sectionIndex);
    setEditSections(newSections);
  };

  const addRow = (sectionIndex) => {
    const totalRows = editSections.reduce(
      (sum, section) => sum + section.rows.length,
      0
    );
    if (totalRows < 10) {
      const newSections = [...editSections];
      newSections[sectionIndex].rows.push({
        id: `list_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        title: "",
        description: "",
      });
      setEditSections(newSections);
    }
  };

  const deleteRow = (sectionIndex, rowIndex) => {
    const newSections = [...editSections];
    newSections[sectionIndex].rows.splice(rowIndex, 1);
    setEditSections(newSections);
  };

  const updateSectionTitle = (sectionIndex, title) => {
    if (title.length <= LIST_SECTION_TITLE_MAX) {
      const newSections = [...editSections];
      newSections[sectionIndex].title = title;
      setEditSections(newSections);
    }
  };

  const updateRow = (sectionIndex, rowIndex, field, value) => {
    const newSections = [...editSections];
    const row = newSections[sectionIndex].rows[rowIndex];
    if (field === "title" && value.length > LIST_ROW_TITLE_MAX) return;
    if (field === "description" && value.length > LIST_ROW_DESCRIPTION_MAX)
      return;
    row[field] = value;
    setEditSections(newSections);
  };

  const handleMessageTypeChange = (newType) => {
    setEditMessageType(newType);

    // Clear the other type's data when switching
    if (newType === "buttons") {
      setEditSections([]);
    } else if (newType === "list") {
      setEditButtons([]);
      setButtonErrors([]);
    }
  };

  const insertIntoBodyAtCursor = (textToInsert) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      setEditBody((prev) => {
        const updated = `${prev}${textToInsert}`;
        validateBody(updated);
        return updated;
      });
      return;
    }

    const start = textarea.selectionStart ?? editBody.length;
    const end = textarea.selectionEnd ?? editBody.length;
    const currentValue = editBody;

    const updated =
      currentValue.slice(0, start) + textToInsert + currentValue.slice(end);
    setEditBody(updated);
    validateBody(updated);

    setTimeout(() => {
      textarea.focus();
      const cursorPos = start + textToInsert.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };

  const handleBodyBoldClick = () => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) {
      setEditBody((prev) => `*${prev}*`);
      return;
    }

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const currentValue = editBody;

    let updated;
    let cursorStart;

    if (start === end) {
      updated =
        currentValue.slice(0, start) + "**" + currentValue.slice(end);
      cursorStart = start + 1;
    } else {
      const selected = currentValue.slice(start, end);
      updated =
        currentValue.slice(0, start) +
        `*${selected}*` +
        currentValue.slice(end);
      cursorStart = start + selected.length + 2;
    }

    setEditBody(updated);
    validateBody(updated);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorStart, cursorStart);
    }, 0);
  };

  const handleBodyEmojiSelect = (emoji) => {
    insertIntoBodyAtCursor(emoji.native || "");
    setShowEmojiPicker(false);
  };

  const handleBodyVariableInsert = (variable) => {
    insertIntoBodyAtCursor(`{{${variable}}}`);
  };

  // Save changes
  const handleSave = () => {
    // Validate all fields
    const isHeaderValid = validateHeader(editHeader);
    const isBodyValid = validateBody(editBody);
    const isFooterValid = validateFooter(editFooter);
    const isAnswerVariableValid = validateAnswerVariable(editAnswerVariable);

    const isButtonsValid = editButtons.every((button, index) =>
      validateButton(button, index)
    );

    if (
      isHeaderValid &&
      isBodyValid &&
      isFooterValid &&
      isButtonsValid &&
      isAnswerVariableValid
    ) {
      // Update parent data
      console.log(`[${id}] Saving changes:`, {
        header: editHeader,
        body: editBody,
        footer: editFooter,
        labels: editButtons,
        sections: editSections,
        listHeader: editListHeader,
        messageType: editMessageType,
      });
      
      if (safeData.onChangeHeader) safeData.onChangeHeader(id, editHeader);
      if (safeData.onChangeBody) safeData.onChangeBody(id, editBody);
      if (safeData.onChangeFooter) safeData.onChangeFooter(id, editFooter);
      if (safeData.onChangeLabels) safeData.onChangeLabels(id, editButtons);
      if (safeData.onChangeSections) safeData.onChangeSections(id, editSections);
      if (safeData.onChangeListHeader)
        safeData.onChangeListHeader(id, editListHeader);
      if (safeData.onChangeMessageType)
        safeData.onChangeMessageType(id, editMessageType);
      if (safeData.onChangeAnswerVariable)
        safeData.onChangeAnswerVariable(id, editAnswerVariable || "");

      // Update local state to reflect changes immediately
      setHeader(editHeader);
      setBody(editBody);
      setFooter(editFooter);
      setLabels(editButtons);
      setSections(editSections);
      setListHeader(editListHeader);
      setMessageType(editMessageType);
      setAnswerVariable(editAnswerVariable || "");

      // Notify parent about section changes if switching to list type
      if (
        editMessageType === "list" &&
        editSections &&
        Array.isArray(editSections)
      ) {
        notifySectionsChange(editSections);
      }

      // Close popup
      setShowEditPopup(false);
      setShowMenu(false);
    }
  };

  // Cancel changes
  const handleCancel = () => {
    // Reset to current local state values
    setEditHeader(header || "");
    setEditBody(body || "");
    setEditFooter(footer || "");
    setEditButtons(normalizeLabelsToStrings(labels));
    setEditSections(sections || []);
    setEditListHeader(listHeader || "");
    setEditMessageType(messageType || "buttons");
    setEditAnswerVariable(answerVariable || "");

    // Clear errors
    setHeaderError("");
    setBodyError("");
    setFooterError("");
    setButtonErrors([]);
    setAnswerVariableError("");

    // Close popup
    setShowEditPopup(false);
    setShowMenu(false);
  };

  // Initialize edit popup
  const handleEdit = () => {
    console.log(`[${id}] Opening edit popup with current data:`, {
      header,
      body,
      footer,
      labels,
      sections,
      listHeader,
      messageType,
    });
    
    setEditHeader(header || "");
    setEditBody(body || "");
    setEditFooter(footer || "");
    // Normalize labels so edit popup always works with plain strings
    setEditButtons(normalizeLabelsToStrings(labels));
    setEditSections(sections || []);
    setEditListHeader(listHeader || "");
    setEditMessageType(messageType || "buttons");
    setEditAnswerVariable(answerVariable || "");
    setButtonErrors(new Array((labels || []).length).fill(""));
    setAnswerVariableError("");
    setShowEditPopup(true);
    setShowMenu(false);
  };

  const handleDelete = () => {
    if (data.onDelete) {
      data.onDelete(id);
    }
    setShowMenu(false);
  };

  const menuIconStyle = {
    position: "absolute",
    top: 6,
    right: 8,
    cursor: "pointer",
    fontSize: 12,
    color: selected ? "#1976d2" : "#6c757d",
    zIndex: 1,
    userSelect: "none",
  };

  const menuStyle = {
    position: "absolute",
    top: 30,
    right: 8,
    background: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 2,
    minWidth: "100px",
  };

  const menuItemStyle = {
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "12px",
    borderBottom: "1px solid #eee",
    transition: "background-color 0.2s",
  };

  // Popup overlay styles
  const popupOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const popupStyle = {
    background: "white",
    borderRadius: "8px",
    padding: "0",
    width: "600px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const errorStyle = {
    color: "#dc3545",
    fontSize: "12px",
    marginTop: "4px",
  };

  const charCountStyle = {
    fontSize: "11px",
    color: "#6c757d",
    textAlign: "right",
    marginTop: "4px",
    marginBottom: "4px",
  };

  const BODY_PREVIEW_MAX = 80;
  const truncateForPreview = (value) =>
    value && value.length > BODY_PREVIEW_MAX
      ? `${value.slice(0, BODY_PREVIEW_MAX)}…`
      : value;

  const renderMarkdownBold = (value) => {
    if (!value) return null;
    const parts = [];
    const regex = /\*([^*]+)\*/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(value)) !== null) {
      if (match.index > lastIndex) {
        parts.push(value.slice(lastIndex, match.index));
      }
      parts.push(
        <strong key={parts.length}>
          {match[1]}
        </strong>
      );
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < value.length) {
      parts.push(value.slice(lastIndex));
    }

    return parts;
  };

  return (
    <>
      <div
        style={{
          background: selected ? "#f8f9fa" : "#f1f3f4",
          border: `1px solid ${selected ? "#1976d2" : "#e0e0e0"}`,
          borderRadius: "6px",
          padding: "8px",
          width: "160px",
          minHeight: "60px",
          boxShadow: selected
            ? "0 4px 8px rgba(25, 118, 210, 0.3)"
            : "0 2px 4px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            cursor: "pointer",
            fontSize: 16,
            color: selected ? "#1976d2" : "#6c757d",
            zIndex: 1,
            userSelect: "none",
            fontWeight: "bold",
            lineHeight: "1",
            display: "flex",
            alignItems: "center",
            height: "11px",
          }}
          onClick={() => setShowMenu(!showMenu)}
          title="Menu"
        >
          ⋮
        </div>

        {showMenu && (
          <div ref={menuRef} style={menuStyle}>
            <div
              style={menuItemStyle}
              onClick={handleEdit}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              ✏️ Edit
            </div>
            <div
              style={{ ...menuItemStyle, borderBottom: "none" }}
              onClick={handleDelete}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f5f5f5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "white")
              }
            >
              🗑️ Delete
            </div>
          </div>
        )}

        <Handle
          type="target"
          position={Position.Left}
          onConnect={(params) => {
            console.log("=== Main Node Handle Connect ===", {
              handleId: id + "-main",
              nodeId: id,
              connectionParams: params,
              sourceNode: params.source,
              targetNode: id,
            });
          }}
          style={{ background: selected ? "#1976d2" : "#6c757d" }}
        />
        <div style={{ textAlign: "left" }}>
          <div
            style={{
              fontWeight: "bold",
              color: selected ? "#1976d2" : "#6c757d",
              marginBottom: "8px",
              fontSize: "11px",
            }}
          >
            Interactive Message (
            {messageType === "buttons" ? "Buttons" : "List"})
          </div>

          {header && header.trim() && (
            <div
              style={{
                fontSize: "12px",
                color: "#000000",
                marginTop: "8px",
                marginBottom: "4px",
              }}
            >
              <div style={{ cursor: "default", textAlign: "left" }}>
                {renderMarkdownBold(header)}
              </div>
            </div>
          )}

          {body && body.trim() && (
            <div
              style={{ fontSize: "14px", color: "#666", marginBottom: "8px" }}
            >
              <div
                style={{
                  cursor: "default",
                  minHeight: "24px",
                  textAlign: "left",
                  whiteSpace: "pre-wrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {renderMarkdownBold(truncateForPreview(body))}
              </div>
            </div>
          )}

          {footer && footer.trim() && (
            <div
              style={{
                fontSize: "12px",
                color: "#000000",
                marginTop: "2px",
                marginBottom: "8px",
              }}
            >
              <div style={{ cursor: "default", textAlign: "left" }}>
                {renderMarkdownBold(footer)}
              </div>
            </div>
          )}
          {answerVariable ? (
            <div style={{ fontSize: "10px", color: "#666", marginBottom: "8px" }}>
              Var: {answerVariable}
            </div>
          ) : null}

          {/* Sections Preview - only show when message type is list */}
          {messageType === "list" &&
            sections &&
            Array.isArray(sections) &&
            sections.length > 0 && (
              <div
                style={{
                  marginTop: "8px",
                  marginBottom: "8px",
                  borderTop: "1px solid #e0e0e0",
                  paddingTop: "8px",
                }}
              >
                {/* List Header */}
                {listHeader && listHeader.trim() && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#495057",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      padding: "4px 6px",
                      backgroundColor: "#e3f2fd",
                      borderRadius: "4px",
                      border: "1px solid #bbdefb",
                    }}
                  >
                    📋 {listHeader}
                  </div>
                )}
                <div
                  style={{
                    fontSize: "10px",
                    color: "#666",
                    marginBottom: "6px",
                    fontWeight: "bold",
                  }}
                >
                  📋 List Sections
                </div>
                {sections.map((section, sectionIndex) => (
                  <div
                    key={section.id || sectionIndex}
                    style={{
                      marginBottom: "6px",
                      padding: "4px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "4px",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    {/* Section Title (truncated for preview) */}
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        color: "#495057",
                        marginBottom: "3px",
                        display: "flex",
                        alignItems: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span style={{ marginRight: "4px" }}>📑</span>
                      {(section.title || `Section ${sectionIndex + 1}`).slice(0, LIST_SECTION_TITLE_MAX)}
                      {(section.title || "").length > LIST_SECTION_TITLE_MAX ? "…" : ""}
                    </div>

                    {/* Section Rows */}
                    {section.rows &&
                      section.rows.length > 0 &&
                      section.rows.map((row, rowIndex) => {
                        const handleId = `${id}-section-${sectionIndex}-row-${rowIndex}`;
                        const isConnected = isHandleConnected(handleId);

                        return (
                          <div
                            key={row.id || rowIndex}
                            style={{
                              fontSize: "9px",
                              color: "#6c757d",
                              marginBottom: "2px",
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                              padding: "2px 4px",
                              background: isConnected
                                ? "#d4edda"
                                : "transparent",
                              borderRadius: "3px",
                            }}
                          >
                            <span style={{ marginRight: "4px" }}>•</span>
                            <span style={{ fontWeight: "bold", maxWidth: "80px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {(row.title || `Row ${rowIndex + 1}`).slice(0, LIST_ROW_TITLE_MAX)}
                              {(row.title || "").length > LIST_ROW_TITLE_MAX ? "…" : ""}:
                            </span>
                            <span
                              style={{
                                marginLeft: "4px",
                                fontStyle: "italic",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "100px",
                              }}
                            >
                              {(row.description || "No description").slice(0, LIST_ROW_DESCRIPTION_MAX)}
                              {(row.description || "").length > LIST_ROW_DESCRIPTION_MAX ? "…" : ""}
                            </span>
                            <Handle
                              type="source"
                              position={Position.Right}
                              id={handleId}
                              onConnect={(params) => {
                                console.log("=== Row Handle Connect ===", {
                                  handleId: handleId,
                                  row: row,
                                  sectionIndex: sectionIndex,
                                  rowIndex: rowIndex,
                                  connectionParams: params,
                                  sourceNode: id,
                                  targetNode: params.target,
                                });
                              }}
                              style={{
                                background: isConnected
                                  ? "#28a745"
                                  : selected
                                  ? "#1976d2"
                                  : "#6c757d",
                                width: "6px",
                                height: "6px",
                                right: "-3px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                border: "1px solid white",
                                opacity: isConnected ? 0.5 : 1,
                                cursor: isConnected
                                  ? "not-allowed"
                                  : "crosshair",
                                pointerEvents: isConnected ? "none" : "auto",
                              }}
                            />
                          </div>
                        );
                      })}

                    {(!section.rows || section.rows.length === 0) && (
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#adb5bd",
                          fontStyle: "italic",
                          marginLeft: "12px",
                        }}
                      >
                        No rows added
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          {/* Show placeholder when all content fields are empty */}
          {(!header || !header.trim()) &&
            (!body || !body.trim()) &&
            (!footer || !footer.trim()) &&
            (messageType === "buttons"
              ? !labels || labels.length === 0
              : !sections || sections.length === 0) && (
              <div
                style={{ fontSize: "12px", color: "#999", marginBottom: "8px" }}
              >
                <div
                  style={{
                    cursor: "default",
                    fontStyle: "italic",
                    textAlign: "left",
                  }}
                >
                  Click Edit to add content
                </div>
              </div>
            )}

          {/* Show Buttons only when message type is buttons */}
          {messageType === "buttons" &&
            labels &&
            Array.isArray(labels) &&
            labels.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  width: "100%",
                }}
              >
                {labels.map((label, index) => {
                  const handleId = id + "-label-" + index;
                  const isConnected = isHandleConnected(handleId);

                  return (
                    <div
                      key={`${id}-label-${index}-${
                        isConnected ? "connected" : "disconnected"
                      }-${data.lastEdgeUpdate || 0}`}
                      style={{
                        position: "relative",
                        background: isConnected ? "#28a745" : "#fd7e14",
                        color: "white",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        width: "100%",
                        textAlign: "center",
                        boxSizing: "border-box",
                        border: `1px solid ${
                          isConnected ? "#1e7e34" : "#e67e22"
                        }`,
                        minHeight: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "8px",
                        opacity: isConnected ? 0.7 : 1,
                      }}
                      title={
                        isConnected
                          ? "This label is already connected"
                          : "Click to connect"
                      }
                    >
                      {typeof label === 'object' && label.label ? label.label : label}
                      <Handle
                        type="source"
                        position={Position.Right}
                        id={id + "-label-" + index}
                        onConnect={(params) => {
                          console.log("=== Label Handle Connect ===", {
                            handleId: id + "-label-" + index,
                            labelText: label,
                            labelIndex: index,
                            connectionParams: params,
                            sourceNode: id,
                            targetNode: params.target,
                          });
                        }}
                        style={{
                          background: isConnected
                            ? "#28a745"
                            : selected
                            ? "#1976d2"
                            : "#6c757d",
                          width: "6px",
                          height: "6px",
                          right: "-3px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "1px solid white",
                          opacity: isConnected ? 0.5 : 1,
                          cursor: isConnected ? "not-allowed" : "crosshair",
                          pointerEvents: isConnected ? "none" : "auto",
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      </div>

      {/* Edit Popup Modal */}
      {showEditPopup &&
        createPortal(
          <div style={popupOverlayStyle} onClick={handleCancel}>
            <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
              {/* Header with title and close button */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "24px 24px 16px 24px",
                  borderBottom: "1px solid #e9ecef",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "white",
                  zIndex: 1,
                  borderRadius: "8px 8px 0 0",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: "#333",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  Interactive Message
                </h3>
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <button
                    onClick={handleSave}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #28a745",
                      borderRadius: "4px",
                      backgroundColor: "#28a745",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: "6px 8px",
                      border: "1px solid #6c757d",
                      borderRadius: "4px",
                      backgroundColor: "white",
                      color: "#6c757d",
                      cursor: "pointer",
                      fontSize: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "28px",
                      height: "28px",
                    }}
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Content Area */}
              <div style={{ padding: "0 24px 24px 24px" }}>
                {/* Header Field */}
                <div style={{ marginTop: "8px", marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Header
                  </label>
                  <input
                    type="text"
                    value={editHeader}
                    onChange={handleHeaderChange}
                    placeholder="Enter header text (max 60 chars)"
                    maxLength={60}
                    style={{
                      ...inputStyle,
                      borderColor: headerError ? "#dc3545" : "#ddd",
                    }}
                  />
                  <div style={charCountStyle}>{editHeader.length}/60</div>
                  {headerError && <div style={errorStyle}>{headerError}</div>}
                </div>

                {/* Body Field */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Body
                  </label>
                  <textarea
                    ref={bodyTextareaRef}
                    value={editBody}
                    onChange={handleBodyChange}
                    placeholder="Enter body text"
                    rows={4}
                    style={{
                      ...inputStyle,
                      borderColor: bodyError ? "#dc3545" : "#ddd",
                      resize: "vertical",
                    }}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "6px",
                      gap: "8px",
                      fontSize: "12px",
                      color: "#555",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={handleBodyBoldClick}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                          backgroundColor: "#f8f9fa",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        <FormatBoldIcon style={{ fontSize: 14 }} />
                        <span>Bold</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          border: "1px solid #dee2e6",
                          backgroundColor: "#ffffff",
                          cursor: "pointer",
                          fontSize: "12px",
                        }}
                      >
                        <InsertEmoticonIcon style={{ fontSize: 16 }} />
                        <span>Emoji</span>
                      </button>
                    </div>
                  </div>
                  <FlowVariableInsertPicker
                    customVariables={availableVariables}
                    onInsert={handleBodyVariableInsert}
                    label="Insert variable into message body"
                  />
                  {showEmojiPicker && (
                    <div
                      style={{
                        position: "fixed",
                        zIndex: 10000,
                        right: 40,
                        bottom: 160,
                      }}
                    >
                      <Picker
                        data={emojiData}
                        onEmojiSelect={handleBodyEmojiSelect}
                        theme="light"
                      />
                    </div>
                  )}
                  <div style={charCountStyle}>{editBody.length}/1024</div>
                  {bodyError && <div style={errorStyle}>{bodyError}</div>}
                </div>

                {/* Footer Field */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Footer
                  </label>
                  <input
                    type="text"
                    value={editFooter}
                    onChange={handleFooterChange}
                    placeholder="Enter footer text (max 60 chars)"
                    maxLength={60}
                    style={{
                      ...inputStyle,
                      borderColor: footerError ? "#dc3545" : "#ddd",
                    }}
                  />
                  <div style={charCountStyle}>{editFooter.length}/60</div>
                  {footerError && <div style={errorStyle}>{footerError}</div>}
                </div>

                {/* Message Type Selector */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Message Type
                  </label>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button
                      type="button"
                      onClick={() => handleMessageTypeChange("buttons")}
                      style={{
                        padding: "8px 16px",
                        border: `2px solid ${editMessageType === "buttons" ? "#1976d2" : "#ddd"
                          }`,
                        borderRadius: "6px",
                        backgroundColor:
                          editMessageType === "buttons" ? "#e3f2fd" : "white",
                        color:
                          editMessageType === "buttons" ? "#1976d2" : "#666",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight:
                          editMessageType === "buttons" ? "bold" : "normal",
                        flex: 1,
                        transition: "all 0.2s",
                      }}
                    >
                      📱 Buttons
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMessageTypeChange("list")}
                      style={{
                        padding: "8px 16px",
                        border: `2px solid ${editMessageType === "list" ? "#1976d2" : "#ddd"
                          }`,
                        borderRadius: "6px",
                        backgroundColor:
                          editMessageType === "list" ? "#e3f2fd" : "white",
                        color: editMessageType === "list" ? "#1976d2" : "#666",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight:
                          editMessageType === "list" ? "bold" : "normal",
                        flex: 1,
                        transition: "all 0.2s",
                      }}
                    >
                      📋 List
                    </button>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                    }}
                  >
                    Choose between interactive buttons or list sections
                  </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontWeight: "bold",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Save answer in a variable
                  </label>
                  <input
                    type="text"
                    value={editAnswerVariable}
                    onChange={(e) => {
                      setEditAnswerVariable(e.target.value);
                      validateAnswerVariable(e.target.value);
                    }}
                    placeholder="e.g. selected_option"
                    style={inputStyle}
                  />
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6c757d",
                      marginTop: "4px",
                    }}
                  >
                    This name will be stored as `answer_variable` for this
                    interactive message using selected button or list row title.
                  </div>
                  {answerVariableError ? (
                    <div style={errorStyle}>{answerVariableError}</div>
                  ) : null}
                </div>

                {/* Buttons Section - only show when message type is buttons */}
                {editMessageType === "buttons" && (
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "bold",
                        color: "#333",
                        fontSize: "14px",
                      }}
                    >
                      Buttons ({editButtons.length}/3)
                    </label>

                    {editButtons.map((button, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "12px",
                          display: "flex",
                          gap: "12px",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={button.label || button}
                            onChange={(e) =>
                              handleButtonChange(index, e.target.value)
                            }
                            placeholder={`Button ${index + 1} text`}
                            style={{
                              ...inputStyle,
                              borderColor: buttonErrors[index]
                                ? "#dc3545"
                                : "#ddd",
                            }}
                          />
                          <div style={charCountStyle}>{button.length}/20</div>
                          {buttonErrors[index] && (
                            <div style={errorStyle}>{buttonErrors[index]}</div>
                          )}
                        </div>
                        <button
                          onClick={() => deleteButton(index)}
                          style={{
                            padding: "8px 12px",
                            border: "1px solid #dc3545",
                            borderRadius: "4px",
                            backgroundColor: "white",
                            color: "#dc3545",
                            cursor: "pointer",
                            fontSize: "12px",
                            marginTop: "8px",
                            minWidth: "40px",
                          }}
                          title="Delete button"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}

                    {editButtons.length < 3 && (
                      <button
                        onClick={addButton}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #28a745",
                          borderRadius: "4px",
                          backgroundColor: "white",
                          color: "#28a745",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "100%",
                        }}
                      >
                        + Add Button
                      </button>
                    )}
                  </div>
                )}

                {/* List Sections - only show when message type is list */}
                {editMessageType === "list" && (
                  <div style={{ marginBottom: "20px" }}>
                    {/* List Header Input */}
                    <div style={{ marginBottom: "16px" }}>
                      <label
                        style={{
                          fontWeight: "bold",
                          color: "#333",
                          fontSize: "14px",
                          marginBottom: "8px",
                          display: "block",
                        }}
                      >
                        List Header
                      </label>
                      <input
                        type="text"
                        value={editListHeader}
                        onChange={handleListHeaderChange}
                        placeholder="Enter list header (max 60 characters)"
                        maxLength={60}
                        style={{
                          ...inputStyle,
                          width: "100%",
                          fontSize: "14px",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "4px",
                        }}
                      >
                        {editListHeader.length}/60 characters
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          fontWeight: "bold",
                          color: "#333",
                          fontSize: "14px",
                        }}
                      >
                        List Sections ({editSections.length}/10)
                      </label>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Total Rows:{" "}
                        {editSections.reduce(
                          (sum, section) => sum + section.rows.length,
                          0
                        )}
                        /10
                      </div>
                    </div>

                    {editSections.map((section, sectionIndex) => (
                      <div
                        key={section.id}
                        style={{
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          padding: "16px",
                          marginBottom: "16px",
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        {/* Section Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "12px",
                          }}
                        >
                          <div style={{ flex: 1, marginRight: "12px" }}>
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) =>
                                updateSectionTitle(sectionIndex, e.target.value)
                              }
                              placeholder={`Section title (max ${LIST_SECTION_TITLE_MAX} chars)`}
                              maxLength={LIST_SECTION_TITLE_MAX}
                              style={{
                                ...inputStyle,
                                width: "100%",
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            />
                            <div style={charCountStyle}>
                              {(section.title || "").length}/{LIST_SECTION_TITLE_MAX}
                            </div>
                          </div>
                          <button
                            onClick={() => deleteSection(sectionIndex)}
                            style={{
                              padding: "6px 10px",
                              border: "1px solid #dc3545",
                              borderRadius: "4px",
                              backgroundColor: "white",
                              color: "#dc3545",
                              cursor: "pointer",
                              fontSize: "12px",
                              minWidth: "30px",
                            }}
                            title="Delete section"
                          >
                            🗑️
                          </button>
                        </div>

                        {/* Section Rows */}
                        <div style={{ marginBottom: "12px" }}>
                          {section.rows.map((row, rowIndex) => (
                            <div
                              key={row.id}
                              style={{
                                border: "1px solid #e9ecef",
                                borderRadius: "6px",
                                padding: "12px",
                                marginBottom: "8px",
                                backgroundColor: "white",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  marginBottom: "8px",
                                }}
                              >
                                <div style={{ flex: 1, marginRight: "8px" }}>
                                  <input
                                    type="text"
                                    value={row.title}
                                    onChange={(e) =>
                                      updateRow(
                                        sectionIndex,
                                        rowIndex,
                                        "title",
                                        e.target.value
                                      )
                                    }
                                    placeholder={`Row title (max ${LIST_ROW_TITLE_MAX} chars)`}
                                    maxLength={LIST_ROW_TITLE_MAX}
                                    style={{
                                      ...inputStyle,
                                      width: "100%",
                                      fontSize: "13px",
                                    }}
                                  />
                                  <div style={charCountStyle}>
                                    {(row.title || "").length}/{LIST_ROW_TITLE_MAX}
                                  </div>
                                </div>
                                <button
                                  onClick={() =>
                                    deleteRow(sectionIndex, rowIndex)
                                  }
                                  style={{
                                    padding: "4px 8px",
                                    border: "1px solid #dc3545",
                                    borderRadius: "4px",
                                    backgroundColor: "white",
                                    color: "#dc3545",
                                    cursor: "pointer",
                                    fontSize: "11px",
                                    minWidth: "25px",
                                  }}
                                  title="Delete row"
                                >
                                  ×
                                </button>
                              </div>
                              <div>
                                <input
                                  type="text"
                                  value={row.description}
                                  onChange={(e) =>
                                    updateRow(
                                      sectionIndex,
                                      rowIndex,
                                      "description",
                                      e.target.value
                                    )
                                  }
                                  placeholder={`Row description (max ${LIST_ROW_DESCRIPTION_MAX} chars)`}
                                  maxLength={LIST_ROW_DESCRIPTION_MAX}
                                  style={{
                                    ...inputStyle,
                                    fontSize: "12px",
                                    color: "#666",
                                  }}
                                />
                                <div style={charCountStyle}>
                                  {(row.description || "").length}/{LIST_ROW_DESCRIPTION_MAX}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add Row Button */}
                        {editSections.reduce(
                          (sum, s) => sum + s.rows.length,
                          0
                        ) < 10 && (
                          <button
                            onClick={() => addRow(sectionIndex)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #17a2b8",
                              borderRadius: "4px",
                              backgroundColor: "white",
                              color: "#17a2b8",
                              cursor: "pointer",
                              fontSize: "12px",
                              width: "100%",
                            }}
                          >
                            + Add Row
                          </button>
                        )}
                      </div>
                    ))}

                    {/* Add Section Button */}
                    {editSections.length < 10 && (
                      <button
                        onClick={addSection}
                        style={{
                          padding: "8px 12px",
                          border: "1px solid #28a745",
                          borderRadius: "4px",
                          backgroundColor: "white",
                          color: "#28a745",
                          cursor: "pointer",
                          fontSize: "12px",
                          width: "100%",
                        }}
                      >
                        + Add Section
                      </button>
                    )}

                    {/* Warning Messages */}
                    {editSections.length >= 10 && (
                      <div
                        style={{
                          color: "#ff9800",
                          fontSize: "12px",
                          marginTop: "8px",
                          textAlign: "center",
                        }}
                      >
                        Maximum 10 sections reached
                      </div>
                    )}
                    {editSections.reduce(
                      (sum, section) => sum + section.rows.length,
                      0
                    ) >= 10 && (
                      <div
                        style={{
                          color: "#ff9800",
                          fontSize: "12px",
                          marginTop: "8px",
                          textAlign: "center",
                        }}
                      >
                        Maximum 10 total rows reached
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
